<template>
  <template v-for="kpi in kpiValues" :key="kpi.kpi">
    <KPICard
      :color="kpiSettingsMap?.[kpi.kpi]?.color!"
      :kpi="kpi.summary.value"
      :kpiFormat="{ format: { type: 'intl-number', style: 'percent' } }"
      :kpiDetails="getDetails(kpi)"
      :fullscreen="fullscreen"
      :isLoading="isLoading"
      :numberOfSpaces="kpi.summary.metadata.numberOfSpaces"
      :title="kpiSettingsMap?.[kpi.kpi]?.title[locale] ?? ''"
      :info="kpiSettingsMap?.[kpi.kpi]?.info?.[locale] ?? ''"
      :showOnMap="selectedKpiType === kpi.kpi"
      :showOnMapEnabled="showOnMapEnabled && isShowOnMapEnabled(kpiSettingsMap?.[kpi.kpi]!)"
      :kpiType="kpi.kpi"
      @toggleShowOnMap="toggleKpiSelection(kpi.kpi)"
    />
  </template>
</template>

<script lang="ts" setup>
import { KpiQuery, KpiResult } from '@/api-client/models/utilization/Contracts'
import { useKpis, useKpisFromQuery } from '../composables/useKpis'
import { computed, toRefs, watch } from 'vue'
import { calculateKpiPerRoomGroup } from '../calculateKpi'
import { useI18n } from 'vue-i18n'
import KPICard from './cards/KPICard.vue'
import { COCLASSGROUPS } from '@/interfaces/coclass.interface'

interface Props {
  fullscreen: boolean
  query: KpiQuery
}

type Emits = {
  (e: 'toggleShowOnMap'): void
  (e: 'missingData', missingData: boolean)
}

const { locale } = useI18n()
const props = defineProps<Props>()
const query = computed(() => props.query)
const emits = defineEmits<Emits>()
const kpis = useKpis()
const { toggleKpiSelection } = kpis
const { kpiSettingsMap, selectedKpiType, isShowOnMapEnabled, showOnMapEnabled } = toRefs(kpis)

const { kpiValues, isLoading, missingData } = useKpisFromQuery(query)
watch(missingData, (missing) => {
  emits('missingData', missing)
})

function getDetails(kpi: KpiResult): { header: string[]; rows: any[][] } {
  const perRoomGroup = calculateKpiPerRoomGroup(kpi)
  const header = [
    { sv: 'Rumstyp', en: 'Room type' },
    { sv: 'Antal rum', en: 'Rooms' },
    { sv: 'Andel', en: 'Part' }
  ] as Record<string, string>[]

  const rows = perRoomGroup.map(({ roomGroup, numberOfSpaces, value }) => [
    roomGroup ? COCLASSGROUPS[roomGroup][locale.value] : 'Okänd',
    numberOfSpaces,
    formatPercentage(value)
  ])

  return { header: header.map((x) => x[locale.value]), rows }
}

function formatPercentage(value: number | string): string {
  return typeof value === 'number' ? `${Math.round(value * 100)}%` : value
}
</script>
